/*
 * AppUpdateModal — host for the update prompt driven by AppUpdateService.
 *
 * Mounts once from App.jsx (sibling of WhatsNewModal), registers itself as the
 * service's modal handler and starts the background watcher. Shows a clean,
 * theme-aware bottom sheet: new-version pill, benefit copy, a store-specific
 * "Update now" button plus "Remind me later" (hourly snooze) and "Skip this
 * version" actions. It only becomes a non-dismissable centered modal when the
 * release is server-flagged critical (Android back / backdrop taps ignored then).
 *
 * Premium look: a SOLID white / navy sheet (radius 28) with a pill CTA; fully
 * dark-mode aware via the management theme, while the brand orange accent
 * (`#F97316`, white text on it) stays fixed across themes so the primary CTA
 * reads the same everywhere.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { BRAND as BRAND_PALETTE, useManagementTheme } from '../helpers/managementTheme';
import { openStoreLink, registerAppUpdateModalHandler, startAppUpdateWatcher } from '../helpers/AppUpdateService';

// Brand orange, held constant across light/dark so the CTA and "new" pill read
// identically on any background (matches the WhatsNewModal accent).
const BRAND = BRAND_PALETTE.base;

const STORE_LABEL_KEYS = {
    appstore: ['app_update_store_appstore', 'Open App Store'],
    testflight: ['app_update_store_testflight', 'Open TestFlight'],
    playstore: ['app_update_store_playstore', 'Open Google Play'],
    tester: ['app_update_store_tester', 'Open App Tester'],
};

export default function AppUpdateModal() {
    const { t } = useTranslation();
    const m = useManagementTheme();
    const styles = useMemo(() => StyleSheet.create(buildStyles(m)), [m]);
    const [payload, setPayload] = useState(null);
    const resolveRef = useRef(null);

    const settle = useCallback((choice) => {
        const resolve = resolveRef.current;

        resolveRef.current = null;
        setPayload(null);

        if (resolve) resolve(choice);
    }, []);

    useEffect(() => {
        const unregister = registerAppUpdateModalHandler((nextPayload) => new Promise((resolve) => {
            resolveRef.current = resolve;
            setPayload(nextPayload);
        }));
        const stopWatcher = startAppUpdateWatcher();

        return () => {
            unregister();
            stopWatcher();
        };
    }, []);

    if (!payload) return null;

    const { forced, latest, current, channel } = payload;
    const [storeKey, storeFallback] = STORE_LABEL_KEYS[channel] || STORE_LABEL_KEYS.tester;

    const handleUpdate = () => {
        // The modal opens the store itself so "Update now" works on every press.
        // Forced mode keeps the modal up (promise stays pending) — the user
        // returning from the store stays blocked until the new build runs.
        openStoreLink(payload.link);

        if (!forced) {
            settle('update');
        }
    };

    // Soft dismiss (backdrop tap / Android back) = "remind me later" — the least
    // destructive choice; the hourly snooze re-surfaces it. Ignored while forced.
    const handleRemind = () => {
        if (forced) return;

        settle('remind');
    };

    // Explicit "skip this version" — stops auto-prompts for this release; the
    // Settings "Update available" row still lets them update whenever they want.
    const handleSkip = () => {
        if (forced) return;

        settle('skip');
    };

    return (
        <Modal
            transparent
            visible
            animationType={forced ? 'fade' : 'slide'}
            statusBarTranslucent
            onRequestClose={handleRemind}
        >
            <View style={[styles.backdrop, forced ? styles.backdropCenter : styles.backdropSheet]}>
                {!forced ? (
                    <TouchableOpacity
                        style={styles.backdropTapArea}
                        activeOpacity={1}
                        onPress={handleRemind}
                    />
                ) : null}
                <View style={[styles.card, forced ? styles.cardCenter : styles.cardSheet]}>
                    <View style={styles.badge}>
                        <Text style={styles.badgeIcon}>🚀</Text>
                    </View>

                    <Text style={styles.title}>
                        {t('app_update_title', { defaultValue: 'Update available' })}
                    </Text>
                    <Text style={styles.subtitle}>
                        {t('app_update_subtitle', { version: latest, defaultValue: 'Version {{version}} is ready to install' })}
                    </Text>

                    <View style={styles.versionRow}>
                        <View style={styles.versionPill}>
                            <Text style={styles.versionPillLabel}>
                                {t('app_update_current_version', { defaultValue: 'Installed' })}
                            </Text>
                            <Text style={styles.versionPillValue}>{current}</Text>
                        </View>
                        <Text style={styles.versionArrow}>→</Text>
                        <View style={[styles.versionPill, styles.versionPillNew]}>
                            <Text style={[styles.versionPillLabel, styles.versionPillLabelNew]}>
                                {t('app_update_latest_version', { defaultValue: 'New' })}
                            </Text>
                            <Text style={[styles.versionPillValue, styles.versionPillValueNew]}>{latest}</Text>
                        </View>
                    </View>

                    <Text style={styles.body}>
                        {t('app_update_body', {
                            defaultValue: 'Please update to get the latest bug fixes, security patches and new features.',
                        })}
                    </Text>

                    {forced ? (
                        <Text style={styles.forcedNote}>
                            {t('app_update_forced_note', {
                                defaultValue: 'This is a required update — please install it to continue using the app.',
                            })}
                        </Text>
                    ) : null}

                    <TouchableOpacity
                        style={styles.updateBtn}
                        onPress={handleUpdate}
                        activeOpacity={0.85}
                        accessibilityRole="button"
                        testID="app-update-now"
                    >
                        <Text style={styles.updateBtnText}>
                            {t('app_update_now', { defaultValue: 'Update now' })}
                            {'  ·  '}
                            {t(storeKey, { defaultValue: storeFallback })}
                        </Text>
                    </TouchableOpacity>

                    {!forced ? (
                        <View style={styles.secondaryRow}>
                            <TouchableOpacity
                                style={styles.secondaryBtn}
                                onPress={handleRemind}
                                activeOpacity={0.7}
                                accessibilityRole="button"
                                testID="app-update-later"
                            >
                                <Text style={styles.secondaryBtnText}>
                                    {t('app_update_remind', { defaultValue: 'Remind me later' })}
                                </Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={styles.secondaryBtn}
                                onPress={handleSkip}
                                activeOpacity={0.7}
                                accessibilityRole="button"
                                testID="app-update-skip"
                            >
                                <Text style={[styles.secondaryBtnText, styles.skipBtnText]}>
                                    {t('app_update_skip', { defaultValue: 'Skip this version' })}
                                </Text>
                            </TouchableOpacity>
                        </View>
                    ) : null}
                </View>
            </View>
        </Modal>
    );
}

/**
 * Style factory bound to the management theme (light + navy dark). Surfaces/text
 * follow the active theme; the brand orange accent is fixed so the CTA reads
 * identically in light and dark.
 *
 * @param {object} m Result of `useManagementTheme()`.
 * @returns {Object.<string, object>} Style map.
 */
const buildStyles = (m) => {
    const c = m.colors;

    return {
        backdrop: {
            flex: 1,
            backgroundColor: 'rgba(15,23,42,0.55)',
        },
        backdropCenter: {
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
        },
        backdropSheet: {
            justifyContent: 'flex-end',
        },
        backdropTapArea: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
        },
        card: {
            backgroundColor: c.surface,
            borderWidth: 1,
            borderColor: c.border,
            paddingHorizontal: 24,
            paddingTop: 28,
            paddingBottom: 20,
            alignItems: 'center',
            ...Platform.select({
                ios: {
                    shadowColor: '#0F172A',
                    shadowOffset: { width: 0, height: 12 },
                    shadowOpacity: m.isDark ? 0.5 : 0.22,
                    shadowRadius: 24,
                },
                android: { elevation: 12 },
                web: { boxShadow: '0 18px 40px rgba(15, 23, 42, 0.25)' },
            }),
        },
        cardCenter: {
            width: '100%',
            maxWidth: 420,
            borderRadius: 28,
        },
        cardSheet: {
            width: '100%',
            borderTopLeftRadius: 28,
            borderTopRightRadius: 28,
            paddingBottom: 32,
        },
        badge: {
            width: 68,
            height: 68,
            borderRadius: 24,
            backgroundColor: `${BRAND}22`,
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 14,
        },
        badgeIcon: { fontSize: 30 },
        title: {
            fontSize: 22,
            fontWeight: '800',
            letterSpacing: -0.4,
            color: c.heading,
            textAlign: 'center',
        },
        subtitle: {
            fontSize: 14,
            color: c.subText,
            textAlign: 'center',
            marginTop: 4,
        },
        versionRow: {
            flexDirection: 'row',
            alignItems: 'center',
            marginTop: 18,
            marginBottom: 14,
        },
        versionPill: {
            borderRadius: 999,
            backgroundColor: c.surfaceAlt,
            borderWidth: 1,
            borderColor: c.border,
            paddingHorizontal: 16,
            paddingVertical: 7,
            alignItems: 'center',
        },
        versionPillNew: {
            backgroundColor: BRAND,
            borderColor: BRAND,
        },
        versionPillLabel: {
            fontSize: 10,
            textTransform: 'uppercase',
            color: c.subText,
            fontWeight: '700',
        },
        versionPillLabelNew: { color: 'rgba(255,255,255,0.88)' },
        versionPillValue: {
            fontSize: 15,
            fontWeight: '800',
            color: c.heading,
        },
        versionPillValueNew: { color: '#FFFFFF' },
        versionArrow: {
            fontSize: 18,
            color: c.subText,
            marginHorizontal: 10,
        },
        body: {
            fontSize: 14,
            color: c.subText,
            textAlign: 'center',
            lineHeight: 20,
        },
        forcedNote: {
            fontSize: 13,
            color: c.danger,
            textAlign: 'center',
            lineHeight: 18,
            marginTop: 12,
            fontWeight: '600',
        },
        updateBtn: {
            alignSelf: 'stretch',
            backgroundColor: BRAND,
            borderRadius: 999,
            height: 54,
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 18,
            ...Platform.select({
                ios: {
                    shadowColor: BRAND_PALETTE.dark,
                    shadowOffset: { width: 0, height: 8 },
                    shadowOpacity: 0.3,
                    shadowRadius: 14,
                },
                android: { elevation: 6 },
            }),
        },
        updateBtnText: {
            color: '#FFFFFF',
            fontSize: 15,
            fontWeight: '800',
        },
        secondaryRow: {
            flexDirection: 'row',
            alignSelf: 'stretch',
            justifyContent: 'space-between',
            marginTop: 6,
        },
        secondaryBtn: {
            flex: 1,
            paddingVertical: 12,
            alignItems: 'center',
        },
        secondaryBtnText: {
            color: c.subText,
            fontSize: 14,
            fontWeight: '700',
        },
        skipBtnText: {
            opacity: 0.85,
        },
    };
};
